import { Component, OnInit, inject, viewChild } from '@angular/core';
import { combineLatest, concatMap, map, of, switchMap,tap} from 'rxjs';
import { AddressFormComponent } from '../address-form/address-form.component';
import { RoundIconComponent } from '../round-icon/round-icon.component';
import { AuthService } from '../../core/services/auth.service';
import { AccountAddressesService } from '../../core/services/account/account-addresses.service';
import { AddressService } from '../../core/services/address.service';
import { IconNameType } from '../icons/icon-name-type.enum';
import { AddressValidationResult } from '../../core/models/core/address-validation-result.model';
import { GenericObject } from '../../core/models/core/generic-object';
import { ErrorsService } from '../../core/services/errors.service';
import { Address } from '../../core/models/core/address.model';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { TranslocoDirective,TranslocoService  } from '@ngneat/transloco';
import { AuditService } from '../../core/services/audit.service';
import { Router } from '@angular/router';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'cwo-add-address-modal',
  standalone: true,
  imports: [AddressFormComponent, RoundIconComponent, TranslocoDirective],
  templateUrl: './add-address-modal.component.html',
  styleUrl: './add-address-modal.component.scss',
})
export class AddAddressModalComponent implements OnInit {
  modalService = inject(ModalService);
  authService = inject(AuthService);
  addressService = inject(AddressService);
  accountAddressesService = inject(AccountAddressesService);
  errorsService = inject(ErrorsService);
  auditService = inject(AuditService);
  private router = inject(Router);
 toastService = inject(ToastService);
  translocoService = inject(TranslocoService);
  iconNameType = IconNameType;
  isAddressFormValid: boolean = false;
  validationResponse?: AddressValidationResult;
  serverErrors: GenericObject<string[]> | undefined;

  readonly addressFormComponent = viewChild.required<AddressFormComponent>('addressFormComponent');
  isAdressBook: boolean = true;

  ngOnInit() {
    this.isAdressBook = this.router.url.includes('address-book') || this.router.url.includes('account');
  }

  closeModal() {
    // this.layoutService.toggleShowAddAddressModal(false);
    this.modalService.closeModal(ModalName.AddAddress);
  }

  handleAddressFormValidation(isFormValid: boolean) {
    this.isAddressFormValid = isFormValid;
  }

  handleFormChanging() {
    this.validationResponse = undefined;
    this.serverErrors = undefined;
  }

  handleOverrideAddress() {
    this.onSubmit(true);
  }

  handleNoThankyou() {
    this.validationResponse = undefined;
  }

  validateNewAddressAndProceed = (address: Address) => {
    return this.addressService.validateNewAddress(address).pipe(

      switchMap((validationResponse) => {
        // Check if validationResponse contains errors
        if (validationResponse?.error) {
          return of(validationResponse); // Return validationResponse to handle it later
        }

        return this.accountAddressesService.addAddress(address).pipe(
            tap(() => {
                    this.toastService.showSuccess(
                      this.translocoService.translate('route.account.address_book.address_added_succesfully')
                    );
                  }),
          map(() => validationResponse) // Continue passing validation response
        );
      }),
      switchMap((validationResponse) => {
        if (!validationResponse?.error) {
          return this.accountAddressesService
            .fetchAddresses()
            .pipe(map(() => validationResponse));
        }
        // Return observable that completes without further processing
        return of(validationResponse);
      })
    );
  };

  onSubmit(overrideAddress?: boolean) {
    this.auditService.createAudit(() => 'Done button clicked on Add New Address modal');
    this.serverErrors = undefined;

    if (this.validationResponse && !overrideAddress) {
      this.addressFormComponent().toggleHighlightOverrideMessage();
      return;
    }
    this.validationResponse = undefined;

    setTimeout(() => {
      if (!this.isAddressFormValid) {
        if (!this.isAddressFormValid) {
          this.addressFormComponent().enableErrors();
        }
        return;
      }

      const address = new Address().deserialize(
        this.addressFormComponent().addressForm.value
      );

      //trimming address name since server allows multiple values with the same name to be added when we have a space in the end
      address.name = address.name?.trim();

      if (overrideAddress || this.isAdressBook) {
        address.force = true;
        
        this.accountAddressesService
          .addAddress(address)
          .pipe(
              tap(() => {
                    this.toastService.showSuccess(
                      this.translocoService.translate('route.account.address_book.address_added_succesfully')
                    );
                  }),
            concatMap(() =>
              combineLatest([
                this.accountAddressesService.fetchAddresses(),
                this.authService.fetchAccount(true),
              ])
            )
          )
          .subscribe({
            next: () => {
              this.closeModal();
            },
            error: (error: any) => {
              const parsedError = this.errorsService.parseErrorResponse(error);
              this.serverErrors = parsedError.errors;
            },
          });
      } else {
        this.validateNewAddressAndProceed(address).subscribe({
          next: (validationResponse) => {
            if (validationResponse?.error) {
              this.addressFormComponent().enableErrors();
              this.validationResponse = validationResponse;
            } else {
              this.closeModal();
            }
          },
          error: (error) => {
            const parsedError = this.errorsService.parseErrorResponse(error);
            this.serverErrors = parsedError.errors;
          },
        });
      }
    }, 0);
  }
}
